import { cloneDeep } from '@fi-shared/utils';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { NgModel } from '@angular/forms';
import { DocumentEditSettingsComponent } from './settings/settings.component';
import { DocumentEditSharingSelectionComponent } from './sharing-selection/sharing-selection.component';
import { AppTextService } from '@fi-shared/app-text';
import { DocumentSettingModel } from '../document-setting.model';
import { DocumentModel } from '../document.model';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { SideListComponent } from '@fi-app/utilities/side-list/side-list.component';
import { DocumentEditGeneralComponent } from './general/general.component';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { DocumentService } from '../document.service';
import { TemplateModel } from '@fi-app/template/template.model';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { FiSnackBarService } from '@fi-shared/fi-shared-services';
import {
    DocumentDialogEditConfirmSaveComponent,
    IDocumentDialogEditConfirmData,
} from './confirm-save/document-confirm-save.component';
import { FormValidatorService } from '@fi-app/utilities/fi-services/form-validator.service';
import { HtmlPreviewService } from '@fi-shared/fi-print-download/html-preview.service';
import { ModuleType } from '@fi-shared/types/module-type';
import { MoreMenuMobileSettingsModel } from '@fi-app/utilities/more-menu-mobile/more-menu-mobile-settings.model';
import { DocumentEditService } from '../document-edit.service';
import { FiMobileAppHelperService } from '@fi-app/utilities/fi-services/mobile-app-helper.service';
import { FiScrollToElementAnimateService } from '@fi-shared/fi-shared-services/scroll-to-element-animate.service';
import { TemplateLayoutService } from '@fi-app/template/template-layout.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { first, startWith, takeUntil } from 'rxjs/operators';
import { FiDialogCloseReason } from '@fi-shared/fi-dialogs/fi-dialogs-close-reason';
import { RollupContentComponent } from '@fi-shared/rollup/rollup-content.component';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { ILightWeightTemplate } from '@fi-app/document-template/document-template';
import { EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { ObjectTitleService } from '@fi-app/utilities/fi-services/object-title.service';
import { IDocumentDefaultTemplateSettings } from '@fi-app/document/document';
import { FiAppSettings } from '@fi-shared/fi-app-settings';
import { groupBy } from '@fi-shared/utils/group-by';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { TextEditorHistoryService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/text-editor-history.service';
import { AdvancedTextHandlerService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/advanced-text-handler.service';
import { FiAIAssistanceService } from '@fi-shared/fi-ai-assistance/fi-ai-assistance.service';
import { FiAIAssistanceHelperService } from '@fi-shared/fi-ai-assistance/fi-ai-assistance-helper.service';
import { IAIRequestHistoryModel } from '@fi-shared/fi-ai-assistance/fi-ai-assistance';
import { BehaviorSubject, delay, Observable, Subject } from 'rxjs';
import { TemplateControlType } from '@fi-app/template/types/template-control-type';

@Component({
    selector: 'fi-document-edit',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-document-edit' },
    standalone: false,
})
export class DocumentEditComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input('document') Document: DocumentModel;
    @Input() defaultDocumentSettings: DocumentSettingModel;
    @Input() defaultTemplateSettings: IDocumentDefaultTemplateSettings;
    @Input() documentTemplates: ILightWeightTemplate[];
    @Input() set recentAIRequests(value: IAIRequestHistoryModel[]) {
        this.fiAIAssistanceHelperService.setResetRequests(value);
    }

    //@ViewChild('documentEditForm', {static: true}) documentEditForm: NgForm;
    //@ViewChild('documentEditFormElement', {static: false}) documentEditFormElement: ElementRef;
    @ViewChild('documentEditSettings', { static: false }) documentEditSettings: DocumentEditSettingsComponent;
    @ViewChild('documentEditSharingSelection', { static: false })
    documentEditSharingSelection: DocumentEditSharingSelectionComponent;
    @ViewChild('sideList', { static: true }) sideList: SideListComponent;
    @ViewChild('documentEditGeneral', { static: true }) documentEditGeneral: DocumentEditGeneralComponent;
    @ViewChild('scrollableElement', { static: false }) scrollableElementRef: ElementRef<HTMLElement>;

    IsCreateMode: boolean = false;
    EditTabs: EditTabsModel;

    ShowLanguageSelector: boolean; // = FI_CUSTOMER_DATA.ContentLanguages.length > 1;
    documentTemplateLayout;
    Device = 800;
    initialTemplate: TemplateModel;
    MoreMenuItems = new MoreMenuMobileSettingsModel();
    MobileLanguages = {
        Show: false,
    };
    selectedLanguage: number;
    isSettingsExpanded = false;
    private readonly DocumentVersionsLimit: number = 100;
    private readonly DocumentVersionsToRemain: number = 20;

    constructor(
        private appTextService: AppTextService,
        private fiLoadingService: LoadingService,
        private fiDialogsService: FiDialogsService,
        private statisticService: StatisticService,
        private saveChangesWarningService: SaveChangesWarningService,
        private documentService: DocumentService,
        private snackBar: FiSnackBarService,
        private formValidatorService: FormValidatorService,
        private htmlPreviewService: HtmlPreviewService,
        private documentEditService: DocumentEditService,
        private mobileAppHelperService: FiMobileAppHelperService,
        private scrollToElementAnimateService: FiScrollToElementAnimateService,
        private templateLayoutService: TemplateLayoutService,
        private stateService: FiStateService,
        private injector: Injector,
        private customerDataService: FiCustomerDataService,
        private objectTitleService: ObjectTitleService,
        private cdr: ChangeDetectorRef,
        private fiUserDataService: FiUserDataService,
        private textEditorHistoryService: TextEditorHistoryService,
        private advancedTextHandlerService: AdvancedTextHandlerService,
        public fiAIAssistanceHelperService: FiAIAssistanceHelperService,
    ) {}

    ngOnInit() {
        this.ShowLanguageSelector = this.customerDataService.customerData.ContentLanguages.length > 1;
        if (!this.Document) {
            this.stateService.go('DocummentModuleList');
            return;
        }

        // save template layout for comparing before document saving to remove unchanged blocks from a document
        if (this.Document.Template) {
            const templateCopy = new TemplateModel(JSON.parse(JSON.stringify(this.Document.Template)), this.injector);
            const documentTemplateLayout = this.templateLayoutService.GetLayout(templateCopy, this.Document);
            this.templateLayoutService.ApplyDocumentControlContentsToTemplate(
                this.Document.DocumentControlContents,
                templateCopy,
            );
            this.templateLayoutService.TemplatePlaceHolderCollectionToLayout(
                templateCopy.PlaceHolderCollection,
                documentTemplateLayout,
            );
        }

        // set template id for new document
        this.IsCreateMode = this.stateService.current.name === FiStateNames.Document.Add;
        if (this.IsCreateMode && this.defaultTemplateSettings && this.defaultTemplateSettings.TemplateId) {
            //https://chainformation.atlassian.net/browse/FI-2340
            const defaultTemplate = this.documentTemplates.find(
                (template) => template.Id === this.defaultTemplateSettings.TemplateId,
            );
            if (defaultTemplate?.HasViewAccess) {
                this.Document.TemplateId = this.defaultTemplateSettings.TemplateId;
            }
        }

        if (this.IsCreateMode && this.defaultDocumentSettings)
            DocumentSettingModel.updateDocumentFromSetting(this.Document, this.defaultDocumentSettings);

        this.Document.ObjectType = ObjectTypes.Document;
        this.initialTemplate = new TemplateModel(JSON.parse(JSON.stringify(this.Document.Template)), this.injector);
        this.statisticService.Visit(ModuleType.Document, ObjectTypes.Document, this.stateService.params.Id);
        this.originalId = this.Document.Id;
        if (this.Document.IsDraft) this.autoSaveId = this.Document.Id;

        this.startAutosave();

        if (this.ShowLanguageSelector)
            this.MoreMenuItems.AddListItem(this.appTextService.get('common.select_lang'), () => {
                this.MobileLanguages.Show = true;
            });

        this.MoreMenuItems.AddListItem(this.appTextService.get('common.save_and_close'), () => this.Save());

        this.MoreMenuItems.AddListItem(this.appTextService.get('common.cancel'), () => window.history.back());

        /*if (!this.Document.Id) {
            this.Document.AccessRightFilterCollection.SelectionMode = AccessRightTabs.QuickChoice;
        }*/

        this.selectedLanguage =
            parseInt(this.stateService.params.languageId) || this.fiUserDataService.getContentLanguage();
        this.updateSelectedLang();
        this.isSettingsExpanded = this.stateService.params.isSettingsExpanded;
        this.initTabsModel();
        this.checkDocumentVersionsAmount();
        this.textEditorHistoryService.resetChangesHistoryData();
        this.subscribeOnRecentRequestsChange();
    }

    setAiRequest($event: MouseEvent, request: string) {
        $event.preventDefault();
        $event.stopPropagation();
        this.fiAIAssistanceHelperService.showAiRequestEditor(request);
    }

    private checkDocumentVersionsAmount() {
        const documentVersionsGroupedByContentLanguageId = groupBy(this.Document.DocumentVersions, 'ContentLanguageId');
        Object.keys(documentVersionsGroupedByContentLanguageId).forEach((key) => {
            documentVersionsGroupedByContentLanguageId[key] = documentVersionsGroupedByContentLanguageId[key].filter(
                (documentVersion) => !documentVersion.IsPublished,
            );
        });
        const contentLangIds = Object.keys(documentVersionsGroupedByContentLanguageId).filter((key) => {
            return documentVersionsGroupedByContentLanguageId[key].length > this.DocumentVersionsLimit;
        });
        if (contentLangIds.length) {
            this.showDeleteVersionsDialog(documentVersionsGroupedByContentLanguageId, contentLangIds);
        }
    }

    private showDeleteVersionsDialog(documentGroups, contentLangIds: string[]) {
        this.fiDialogsService
            .confirm({
                headerText: this.appTextService.get('document.delete_old_versions_header'),
                messageText: this.appTextService.get('document.delete_old_versions_message', {
                    '{0}': this.DocumentVersionsLimit,
                    '{1}': this.DocumentVersionsToRemain,
                }),
                yesButtonText: this.appTextService.get('document.delete_old_versions'),
                noButtonText: this.appTextService.get('common.skip_for_now'),
            })
            .onYesButtonClick(() => this.deleteOldVersions(documentGroups, contentLangIds));
    }

    private deleteOldVersions(documentGroups, contentLangIds: string[]) {
        const documentVersionToDelete = [];
        contentLangIds.forEach((langId) => {
            documentVersionToDelete.push(
                ...documentGroups[langId].slice(0, documentGroups[langId].length - this.DocumentVersionsToRemain),
            );
        });
        const documentVersionToDeleteIds = documentVersionToDelete.map((docVersions) => +docVersions.Id);
        this.documentService
            .deleteDocumentVersions(documentVersionToDeleteIds)
            .then(() => this.removeDocumentVersionsFromDocument(documentVersionToDeleteIds));
    }

    private removeDocumentVersionsFromDocument(documentVersionToDeleteIds: number[]) {
        this.Document.DocumentVersions = this.Document.DocumentVersions.filter((documentVersion) => {
            return !documentVersionToDeleteIds.includes(documentVersion.Id);
        });
        this.cdr.markForCheck();
    }

    private initTabsModel(): void {
        const title: string = this.IsCreateMode
            ? this.objectTitleService.getCreateTitle(this.Document.ObjectType)
            : this.objectTitleService.getEditTitle(this.Document.Name);
        this.EditTabs = new EditTabsModel(title);
    }

    ngAfterViewInit(): void {
        this.documentEditSharingSelection.accessRightFilters.onInitialized$.pipe(first()).subscribe(() => {
            this.saveChangesWarningService.startForMultipleObjects([
                () => this.getDocumentForChangesCheck(),
                () => this.documentEditSettings.PublicationSetting,
                () => this.documentEditSharingSelection.AccessRightFilterSettings,
            ]);
        });
    }

    private isTextControlType(controlType: TemplateControlType): boolean {
        return controlType === TemplateControlType.PlainText || controlType === TemplateControlType.HtmlText;
    }

    private getDocumentForChangesCheck() {
        const documentCopy = cloneDeep(this.Document);
        documentCopy.DocumentControlContents = documentCopy.DocumentControlContents.map((item) => {
            if (this.isTextControlType(item.ControlType)) {
                item.Value.Text = this.standardizeCCMTextItemMediaModels(item.Value.Text);
                item.Value.AdditionalText = this.standardizeCCMTextItemMediaModels(item.Value.AdditionalText);
            }
            return item;
        });
        return documentCopy;
    }

    private standardizeCCMTextItemMediaModels(content: string): string {
        if (!content?.length) {
            return content;
        }
        const tempWrapper = document.createElement('div');
        tempWrapper.innerHTML = content;

        const mediaItems = tempWrapper.querySelectorAll('[ccm-text-item-media-model]');
        if (mediaItems && mediaItems.length) {
            Array.from(mediaItems).forEach((mediaItem) => {
                const ccmTextItem = document.createElement('span');

                // remove ChangedOn property to avoid issues with serialization
                const textItemModelObj = JSON.parse(mediaItem.getAttribute('ccm-text-item-media-model'));
                if (textItemModelObj.file?.ChangedOn) {
                    delete textItemModelObj.file.ChangedOn;
                }
                ccmTextItem.setAttribute('ccm-text-item-media-model', JSON.stringify(textItemModelObj));

                const classList = mediaItem.className.split(' ');
                const alignmentClass = classList.find((className) =>
                    className.startsWith('fi-text-item-media-item-alignment'),
                );
                const sizeClass = classList.find((className) => className.startsWith('fi-text-item-media-item-size'));
                ccmTextItem.classList.add(alignmentClass);
                ccmTextItem.classList.add(sizeClass);
                mediaItem.replaceWith(ccmTextItem);
            });
        }
        return tempWrapper.innerHTML;
    }

    //submitted = false;

    getDocumentName() {
        const defaultId = this.fiUserDataService.getContentLanguage();
        const documentName = this.Document.Names.find((item) => item.ContentLanguageId === defaultId);
        return documentName?.Value;
    }

    openScrollToSettings() {
        this.documentEditSettings.pageCollapsableContentComponent.rollupContent.openContent();
        this.scrollToElementAnimateService.scrollToElementBottom(
            this.documentEditSettings.elementRef.nativeElement,
            this.scrollableElementRef.nativeElement,
        );
    }

    openScrollToSharingSelection() {
        this.documentEditSharingSelection.pageCollapsableContentComponent.rollupContent.openContent();
        this.scrollToElementAnimateService.scrollToElementBottom(
            this.documentEditSharingSelection.elementRef.nativeElement,
            this.scrollableElementRef.nativeElement,
        );
    }

    /*saveClick($event) {
        this.submitted = true;
        this.documentEditForm.onSubmit($event);
        this.Save();
        this.documentEditGeneral.formSubmitted();
    }*/

    showSaveConfirmDialog() {
        const dialogContentData: IDocumentDialogEditConfirmData = {
            document: this.Document,
            documentName: this.getDocumentName(),
            openSettings: () => this.openScrollToSettings(),
            openSharing: () => this.openScrollToSharingSelection(),
            sharingText: this.documentEditSharingSelection.selectedSelectionTitle,
        };
        return new Promise<void>((resolve, reject) => {
            this.fiDialogsService
                .custom({
                    headerText: this.appTextService.get('dialog.header_confirm'),
                    yesButtonText: this.appTextService.get('document.save_page'),
                    contentComponentRef: DocumentDialogEditConfirmSaveComponent,
                    contentComponentData: dialogContentData,
                })
                .afterClosed((result) => {
                    if (result.reason !== FiDialogCloseReason.NoButton) resolve();
                    else reject();
                });
        });
    }

    onValidateError(inputsWithErrors: NgModel[]) {
        const valueAccessors = inputsWithErrors.map((ngModel) => ngModel.valueAccessor);
        let errorMessage =
            this.documentEditSharingSelection.accessRightFilters.getErrorMessage() ||
            this.appTextService.get('common.error_all_mandatory');

        const documentNameNgModel = inputsWithErrors.find((ngModel) => ngModel.name === 'document-name');
        if (documentNameNgModel?.errors?.required && !this.documentEditGeneral.isContentEmpty()) {
            const preferredLangId = this.fiUserDataService.getContentLanguage();
            const langName = this.sideList.ccmSideList.Data.find((item) => item.Id === preferredLangId).Name;
            errorMessage = this.appTextService.get('document.name_in_language_required', {
                '{lang_name}': langName,
            });
        }

        if (valueAccessors.indexOf(this.documentEditSharingSelection.accessRightFilters) !== -1) {
            this.documentEditSharingSelection.openSharingSelection().then(() => {
                this.scrollToElementAnimateService.scrollToElementBottom(
                    this.documentEditSharingSelection.pageCollapsableContentComponent.rollupContent.rollupContentElement
                        .nativeElement,
                    this.scrollableElementRef.nativeElement,
                );
            });
        }

        this.fiDialogsService.notify({
            messageText: errorMessage,
            headerText: this.appTextService.get('common.warning'),
        });
    }

    Save() {
        this.showSaveConfirmDialog().then(() => this.processSave());
    }

    processSave() {
        this.stopAutoSave();
        this.textEditorHistoryService.resetChangesHistoryData();
        this.buildDocumentForSave(this.Document);
        if (this.Document.IsPublished && !this.Document.AccessRightFilterCollection.IsAnyRightsSelected())
            this.Document.IsPublished = false;

        this.fiLoadingService.startLoading();

        const savingIsDraft = this.Document.IsDraft;
        const savingId = this.Document.Id;

        if (this.Document.IsDraft) {
            this.Document.IsDraft = false;
            this.Document.Id = this.autoSaveId;
        }

        const isNew = !this.Document.Id;

        return this.documentService.SaveDocument(this.Document, this.selectedLanguage).then(
            () => {
                this.deleteAutoSavedDocument();
                //this.Document.Id = data;
                this.documentEditSharingSelection.accessRightFilters.OnAccessRightFilterCollectionSaved();

                const ScreenHTML = this.htmlPreviewService.getPreviewHtml(document.getElementById('document_view'));
                this.documentService.GenerateDocumentPreviewFileImage(this.Document.Id, ScreenHTML);

                this.saveChangesWarningService.stop();
                if (this.stateService.params.closeIFrameAfterSave === 'true') {
                    if (isNew) this.mobileAppHelperService.reloadPriorityFeed();

                    this.mobileAppHelperService.closeIFrame();
                } else {
                    this.stateService.go(FiStateNames.Document.List);
                    const textId = this.Document.PublicationSetting.IsShared
                        ? 'document.page_published'
                        : 'document.page_saved';
                    const ref = this.snackBar.showSnackbar({
                        textMessage: this.appTextService.get(textId),
                        buttonText: this.appTextService.get('common.view'),
                    });
                    ref.onAction().subscribe(() => {
                        this.stateService.go(FiStateNames.Document.View, { Id: this.Document.Id });
                    });
                }
            },
            () => {
                this.fiLoadingService.finishLoading();
                this.Document.IsDraft = savingIsDraft;
                this.Document.Id = savingId;
                this.startAutosave();
            },
        );
    }

    buildDocumentForSave(documentToSave: DocumentModel) {
        this.documentEditService.TemplateToDocument(this.Document.Template, documentToSave, this.initialTemplate);
    }

    autoSaveTimeoutId: NodeJS.Timeout;

    startAutosave() {
        this.autoSaveTimeoutId = setTimeout(() => {
            this.autoSaveDocument();
        }, FiAppSettings.AutoSaveInterval);
    }

    // document autosave
    autoSavePromise: Promise<void>;
    autoSaveId: number;
    originalId: number;
    shouldAutoSaveOnContentChanged = false;

    autoSaveDocument() {
        if (this.Document.Template && this.shouldAutoSaveOnContentChanged) {
            const saveDoc = JSON.parse(JSON.stringify(this.Document));
            this.buildDocumentForSave(saveDoc);
            saveDoc.Id = this.autoSaveId;
            saveDoc.IsDraft = true;
            if (this.originalId) saveDoc.OriginalId = this.originalId;

            this.autoSavePromise = this.documentService.SaveDocument(saveDoc, this.selectedLanguage).then((data) => {
                this.autoSaveId = data;
                this.shouldAutoSaveOnContentChanged = false;
                this.startAutosave();
            });
        } else {
            this.startAutosave();
        }
    }

    stopAutoSave() {
        if (this.autoSavePromise) {
            this.autoSavePromise.then(
                () => clearTimeout(this.autoSaveTimeoutId),
                () => clearTimeout(this.autoSaveTimeoutId),
            );
        } else {
            clearTimeout(this.autoSaveTimeoutId);
        }
    }

    deleteAutoSavedDocument() {
        /*
        if (this.skipDeletingAutoSavedDocuments)
            return;
*/
        if (this.autoSaveId) {
            /*window.history.replaceState({
                autoSaveId: undefined
            }, document.title);*/
            if (this.autoSavePromise) {
                this.autoSavePromise.then(
                    () => {
                        this.documentService.deleteDocuments([this.autoSaveId]);
                        this.autoSaveId = undefined;
                    },
                    () => {
                        this.documentService.deleteDocuments([this.autoSaveId]);
                        this.autoSaveId = undefined;
                    },
                );
            } else {
                this.documentService.deleteDocuments([this.autoSaveId]);
                this.autoSaveId = undefined;
            }
        }
        this.documentService.raiseAutoSavedDocumentRemoved({
            OriginalId: this.Document.IsDraft ? this.Document.OriginalId : this.Document.Id,
        });
    }

    documentTemplateChange() {
        this.initialTemplate = new TemplateModel(JSON.parse(JSON.stringify(this.Document.Template)), this.injector);
    }

    contentChange() {
        this.shouldAutoSaveOnContentChanged = true;
    }

    onLanguageChange($event) {
        this.selectedLanguage = $event;
        this.updateLanguageState();
    }

    updateLanguageState() {
        // Use native history api instead "state service" because "state service" invokes state reload, but we need just to update current url
        history.replaceState(
            {},
            document.title,
            this.stateService.href(this.stateService.current.name, {
                Id: this.Document.Id || this.stateService.params.Id,
                languageId: this.selectedLanguage,
            }),
        );
        this.updateSelectedLang();
    }

    private updateSelectedLang() {
        this.advancedTextHandlerService.setCurrentContentLangId(this.selectedLanguage);
    }

    documentVersionsOpened($event: RollupContentComponent) {
        this.scrollToElementAnimateService.scrollToElementBottom(
            $event.rollupContentElement.nativeElement,
            this.scrollableElementRef.nativeElement,
        );
    }

    //private skipDeletingAutoSavedDocuments = false;
    documentVersionClick($event) {
        this.saveChangesWarningService.stop();
        /*
        // don't delete draft versions on document version click
        this.skipDeletingAutoSavedDocuments = true;
        */
    }

    private _resentRequests$ = new BehaviorSubject<IAIRequestHistoryModel[]>([]);
    resentRequests$: Observable<IAIRequestHistoryModel[]> = this._resentRequests$.asObservable();
    private subscribeOnRecentRequestsChange() {
        this.fiAIAssistanceHelperService.resentRequests$
            .pipe(
                delay(0), // ExpressionChangedAfterItHasBeenCheckedError fix
                takeUntil(this.ngOnDestroy$),
            )
            .subscribe((res: IAIRequestHistoryModel[]) => this._resentRequests$.next(res));
    }

    protected get isAIAssistanceAvailable$(): Observable<boolean> {
        return this.fiAIAssistanceHelperService.isAIAssistanceVisible$;
    }

    protected getRecentRequestMessage(recentRequest: IAIRequestHistoryModel) {
        return recentRequest.Summary ?? recentRequest.Message;
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.stopAutoSave();
        this.deleteAutoSavedDocument();
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
